import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useAuth } from 'contexts/AuthContext';
import { getCachedUsers } from 'master/userAccountStore';
import {
  PageContainer,
  CommonCss,
  SplitLayoutContainer,
  LeftArea,
  RightArea,
  ActionButton,
} from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { uiColor } from 'styles/uiColor';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import MonthlyBudgetDialog from 'components/budget/MonthlyBudgetDialog';
import {
  TreePanel,
  PanelHead,
  TreeScroll,
  TreeRow,
  ToggleBtn,
  Caret,
  LeafDot,
  TreeLabel,
  RightStack,
  GridPanel,
  GridBody,
  Meta,
  TreeHeadActions,
  TreeToggleBtn,
  DetailToolbar,
  inputAvailStatusColor,
} from 'components/budget/BudgetFormationChrome.styles';

import {
  MONTHLY_BUDGET_LABEL,
  buildMonthlyRowsFromMasters,
  findUnallocRows,
  loadMonthlyBudgets,
} from 'budget/budgetMonthlyBudgetStore';
import { showAlert, showConfirm } from 'utils/toastBridge';
import useScrollbarHoverProps from 'hooks/useScrollbarHoverProps';
import {
  BUDGET_TYPE_DEFAULT,
  loadSubjectsByFilter,
} from 'budget/budgetStructureStore';
import {
  ACCOUNTING_MASTER_CHANGED_EVENT,
  acctClassOptions,
  defaultAcctClassCode,
  acctClassLabel,
} from 'budget/budgetAcctStore';
import {
  BUDGET_ROUNDS_CHANGED_EVENT,
  pickDefaultBudgetKindCode,
  refreshBudgetKindOptions,
  resolveIncomeRequestContext,
  resolveIncomeInputStatusLabel,
  getIncomeDeptSubmitMap,
  submitIncomeRequestDepts,
  cancelIncomeRequestDepts,
} from 'budget/budgetRoundStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import {
  ensureGnkIncomeRequestBootstrap,
  isGnkTenant,
} from 'budget/seed/gnkIncomeRequests';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadFunds, loadFundPackages } from 'budget/budgetFundStore';
import {
  buildSubjectDispCode,
  buildSubjectDispName,
} from 'budget/budgetSubjectHistoryStore';
import { isElementVisible } from 'utils/domVisibility';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import {
  isCalcBasisEditable,
  isCalcAmtEditable,
  formatCalcBasisCommas,
} from 'utils/calcFormula';
import {
  TREE_MODE_OPTIONS,
  CALC_LEVEL_OPTIONS,
  defaultMasterRow,
  defaultDetailRow,
  loadIncomeRequests,
  filterIncomeRequests,
  saveIncomeRequests,
  validateIncomeRequests,
  enrichMasterRow,
  rollupCalcDetails,
  sumTopLevelBudgetAmt,
  createRequestUid,
  buildIncomeRequestTreeFromLines,
} from 'budget/budgetIncomeRequestStore';
import {
  attachBaselineAmounts,
  withBaselineAmounts,
  deriveFinalAmt,
} from 'budget/budgetBaselineStore';
import { seedLastRoundDraftIfEmpty } from 'budget/budgetLastRoundSeed';
import { isLastRoundMode } from 'budget/budgetTenantSettingsStore';

/** 재원 팝업: 항상 재원 + 재원구성 탭 */

const MASTER_GRID_ID = 'budget-income-req-master-v2';
/** 부서·사업·재원 코드열 기본 숨김 — 헤더 메뉴에서 다시 표시 가능. 과목코드는 표준코드라 기본 노출 */
const MASTER_DEFAULT_HIDDEN_COLS = ['deptCode', 'projectCode', 'fundCode'];
const DETAIL_GRID_ID = 'budget-income-req-detail';
const TREE_ROOT_ID = '__ROOT__';

/** 수입편성요구: 원→천원 절상 */
const CALC_OPTS = { inoutDiv: '수입' };

/** 검색조건·그리드 표시용: 코드 · 명칭 */
function formatCodeName(code, name) {
  const c = String(code || '').trim();
  const n = String(name || '').trim();
  if (c && n) return `${c} · ${n}`;
  return c || n || '';
}

/** 멀티 선택 표시 — 많으면 앞 2건 + 외 N건 */
function formatMultiCodeName(items) {
  const list = (items || [])
    .map((it) => formatCodeName(it.code, it.name))
    .filter(Boolean);
  if (!list.length) return '';
  if (list.length <= 2) return list.join(', ');
  return `${list.slice(0, 2).join(', ')} 외 ${list.length - 2}건`;
}

function joinCodes(values) {
  return (values || [])
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(',');
}

/** 로그인 사용자의 주관부서 (사용자관리 마스터) */
function resolveLoginUserDept(tenantCode, user) {
  const loginId = String(user?.id || user?.username || '').trim();
  if (!loginId || !tenantCode) return { deptCode: '', deptName: '' };
  const hit = getCachedUsers(tenantCode).find(
    (u) => String(u.loginId || '').trim() === loginId
  );
  if (!hit) return { deptCode: '', deptName: '' };
  return {
    deptCode: String(hit.deptCode || '').trim(),
    deptName: String(hit.deptName || '').trim(),
  };
}

function stripTreeFields(n) {
  if (!n) return n;
  const { subRows, _treeId, ...rest } = n;
  return rest;
}

function flattenSubtree(node) {
  const out = [];
  const walk = (n) => {
    if (!n) return;
    out.push(stripTreeFields(n));
    (n.subRows || []).forEach(walk);
  };
  walk(node);
  return out;
}

function findTreeNode(nodes, id) {
  for (const n of nodes || []) {
    const tid = n._treeId || `${n.budgetType || ''}::${n.subjectCode || n.projectCode || ''}`;
    if (tid === id) return n;
    const hit = findTreeNode(n.subRows, id);
    if (hit) return hit;
  }
  return null;
}

function treeNodeId(n) {
  return n?._treeId || `${n?.budgetType || ''}::${n?.subjectCode || n?.projectCode || ''}`;
}

function collectAllExpandableIds(nodes) {
  const ids = [];
  const walk = (arr) => {
    (arr || []).forEach((n) => {
      if (n.subRows?.length) {
        ids.push(treeNodeId(n));
        walk(n.subRows);
      }
    });
  };
  walk(nodes);
  return ids;
}

function TreeNodes({ nodes, depth, expandedIds, selectedTreeId, onToggle, onSelect }) {
  return (nodes || []).map((n) => {
    const id = n._treeId || `${n.budgetType || ''}::${n.subjectCode || n.projectCode || ''}`;
    const hasChildren = !!(n.subRows && n.subRows.length);
    const expanded = expandedIds.has(id);
    const code = n.subjectCode || n.projectCode || '';
    const name = n.subjectName || n.projectName || '';
    const level = n.levelName ? `[${n.levelName}] ` : '';
    const label = `${level}${code} ${name}`.trim();
    return (
      <React.Fragment key={id}>
        <TreeRow $depth={depth} $active={selectedTreeId === id} onClick={() => onSelect(n)}>
          {hasChildren ? (
            <ToggleBtn
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(id);
              }}
            >
              {expanded ? '−' : '+'}
            </ToggleBtn>
          ) : (
            <LeafDot>•</LeafDot>
          )}
          {hasChildren ? <Caret>{expanded ? '▼' : '▶'}</Caret> : <Caret />}
          <TreeLabel title={label}>{label}</TreeLabel>
        </TreeRow>
        {hasChildren && expanded && (
          <TreeNodes
            nodes={n.subRows}
            depth={depth + 1}
            expandedIds={expandedIds}
            selectedTreeId={selectedTreeId}
            onToggle={onToggle}
            onSelect={onSelect}
          />
        )}
      </React.Fragment>
    );
  });
}

function StructureTree({
  rootLabel,
  nodes,
  expandedIds,
  selectedTreeId,
  onToggle,
  onSelectRoot,
  onSelectNode,
}) {
  const scrollbarHover = useScrollbarHoverProps();
  return (
    <TreeScroll {...scrollbarHover}>
      <TreeRow $depth={0} $active={selectedTreeId === TREE_ROOT_ID} onClick={onSelectRoot}>
        <LeafDot>⌂</LeafDot>
        <TreeLabel>{rootLabel}</TreeLabel>
      </TreeRow>
      <TreeNodes
        nodes={nodes}
        depth={1}
        expandedIds={expandedIds}
        selectedTreeId={selectedTreeId}
        onToggle={onToggle}
        onSelect={onSelectNode}
      />
    </TreeScroll>
  );
}

/**
 * 수입예산편성요구
 * - 좌: 사업/품목 구분 — 마스터 입력 사업·과목을 확정 구조(관·항·세항·목·세목 / 정책·단위·세부·내역)로 트리화
 * - 조회: 예산차수(결합코드) 선택 · 적용일/입력마감일 확인 · 기간 내만 입력
 * - 상: 마스터(부서·사업·과목·재원·예산액 천원)
 * - 하: 산출 디테일(레벨1~5 · 산출기초 사칙 → 금액 · 상위 합산)
 */
const BudgetIncomeRequestInner = () => {
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  /** 시스템 마스터(admin)만 검색 부서 변경 가능 — 일반은 소속 부서 고정 */
  const canChangeSearchDept = !!isMaster;
  const sessionDept = useMemo(
    () => resolveLoginUserDept(tenantCode, user),
    [tenantCode, user]
  );
  const masterRef = useRef(null);
  const detailRef = useRef(null);
  const allLinesRef = useRef([]);
  const selectedUidRef = useRef('');
  /** F7 조회 시점 검색조건 스냅샷 */
  const searchFilterRef = useRef({
    deptCode: '',
    projectCode: '',
    subjectCode: '',
    fundUid: '',
    fundPkgUid: '',
    fundCode: '',
  });
  const year = getCurrentFiscalYear();
  const [acctMasterTick, setAcctMasterTick] = useState(0);
  const classOptions = useMemo(
    () => acctClassOptions(tenantCode),
    [tenantCode, acctMasterTick]
  );
  const defaultAcctClass = useMemo(
    () => defaultAcctClassCode(tenantCode),
    [tenantCode, acctMasterTick]
  );

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      fiscalYear: year,
      budgetKindCode: '',
      applyDateDisp: '',
      inputDeadlineDisp: '',
      inputAvailStatus: '',
      treeMode: 'SUBJECT',
      acctClass: defaultAcctClass,
      // 검색: 부서·사업·과목·재원 (코드+명칭 표시용 Disp, 빈 값 = 전체)
      deptCode: '',
      deptName: '',
      deptDisp: '',
      projectCode: '',
      projectName: '',
      projectDisp: '',
      subjectCode: '',
      subjectName: '',
      subjectDisp: '',
      fundUid: '',
      fundPkgUid: '',
      fundCode: '',
      fundName: '',
      fundDisp: '',
    }),
    [tenantCode, year, defaultAcctClass]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue, watch } = methods;
  const watchedTreeMode = watch('treeMode');
  const watchedFiscalYear = watch('fiscalYear');
  const watchedKindCode = watch('budgetKindCode');
  const watchedAcctClass = watch('acctClass');
  const watchedDeptCode = watch('deptCode');

  const [kindOptions, setKindOptions] = useState([]);
  const [roundCtx, setRoundCtx] = useState(() =>
    resolveIncomeRequestContext(tenantCode, year, '')
  );
  const canEditRef = useRef(false);
  canEditRef.current = !!roundCtx.canEdit;
  /** 요구단계 부서별 제출Yn — 제출된 부서는 행 단위 수정 불가 */
  const deptSubmitMapRef = useRef(new Map());
  const selectedDeptCodeRef = useRef('');
  const [deptSubmitTick, setDeptSubmitTick] = useState(0);
  const isDeptEditableRef = useRef(() => false);

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: () =>
      canEditRef.current
        ? null
        : roundCtx.editBlockReason || '현재 예산차수에서는 입력할 수 없습니다.',
    guardDelete: () => {
      if (!canEditRef.current) {
        return roundCtx.editBlockReason || '현재 예산차수에서는 삭제할 수 없습니다.';
      }
      const row = masterRef.current?.getSelectedRow?.();
      if (row?.deptCode && !isDeptEditableRef.current(row.deptCode)) {
        return `부서 ${row.deptCode}는 이미 제출되어 삭제할 수 없습니다. 「제출」버튼으로 제출취소 후 가능합니다.`;
      }
      return null;
    },
  });

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  // 소속 부서 기본값 — 일반은 소속 고정, 마스터(admin)는 빈 값(전체 부서)
  useEffect(() => {
    if (canChangeSearchDept) {
      // admin: 검색 부서 미지정 = 전체 조회
      return;
    }
    const code = sessionDept.deptCode;
    const name = sessionDept.deptName;
    setValue('deptCode', code);
    setValue('deptName', name);
    setValue('deptDisp', formatCodeName(code, name));
  }, [sessionDept, canChangeSearchDept, setValue]);

  useEffect(() => {
    const refresh = (e) => {
      if (e.detail?.tenantCode === tenantCode && e.detail?.axis === 'class') {
        setAcctMasterTick((n) => n + 1);
      }
    };
    window.addEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
  }, [tenantCode]);

  // 회계구분 마스터의 사용중 옵션·기본값을 FormArea에 반영
  useEffect(() => {
    const current = getValues('acctClass');
    if (!classOptions.some((o) => o.value === current)) {
      setValue('acctClass', defaultAcctClass);
    }
  }, [classOptions, defaultAcctClass, getValues, setValue]);

  const syncKindOptions = useCallback(async () => {
    const fy = String(getValues('fiscalYear') || year || '').trim();
    const tc = getValues('tenantCode') || tenantCode;
    if (!tc || !fy) {
      setKindOptions([]);
      return [];
    }
    const opts = await refreshBudgetKindOptions(tc, fy);
    setKindOptions(opts);
    const current = String(getValues('budgetKindCode') || '').trim();
    const next = pickDefaultBudgetKindCode(opts, current);
    if (next !== current) {
      setValue('budgetKindCode', next, { shouldDirty: false });
    }
    return opts;
  }, [getValues, year, tenantCode, setValue]);

  // 회계연도·기관 변경 시 결합코드 옵션 적재 (API 캐시가 비어 있으면 동기 list 는 빈 배열)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await syncKindOptions();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantCode, watchedFiscalYear, year, syncKindOptions]);

  useEffect(() => {
    const onChanged = (e) => {
      const d = e.detail || {};
      const fy = String(getValues('fiscalYear') || year || '').trim();
      const tc = getValues('tenantCode') || tenantCode;
      if (d.tenantCode && d.tenantCode !== tc) return;
      if (d.fiscalYear && String(d.fiscalYear) !== fy) return;
      syncKindOptions();
    };
    window.addEventListener(BUDGET_ROUNDS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(BUDGET_ROUNDS_CHANGED_EVENT, onChanged);
  }, [syncKindOptions, getValues, year, tenantCode]);

  // 선택 차수 → 적용일·마감일·입력가능 여부
  useEffect(() => {
    const tenant = tenantCode;
    const fy = watchedFiscalYear || year;
    const ctx = resolveIncomeRequestContext(tenant, fy, watchedKindCode || '');
    setRoundCtx(ctx);
    setValue('applyDateDisp', ctx.applyDateDisp || '');
    setValue('inputDeadlineDisp', ctx.inputDeadlineDisp || '');
  }, [tenantCode, watchedFiscalYear, watchedKindCode, year, setValue]);

  const [treeNodes, setTreeNodes] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set([TREE_ROOT_ID]));
  const [selectedTreeId, setSelectedTreeId] = useState(TREE_ROOT_ID);
  const [allLines, setAllLines] = useState([]);
  const [selectedUid, setSelectedUid] = useState('');
  const [lookup, setLookup] = useState({ open: false, type: null, target: 'grid' });
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  /** 월별예산 팝업 오픈 스냅샷 (검색조건·마스터 동일 반영) */
  const [monthlyPayload, setMonthlyPayload] = useState(null);

  allLinesRef.current = allLines;
  selectedUidRef.current = selectedUid;

  // 입력마감 우측 · 현재 입력 가능 상태 (확정/제출/적용일·마감)
  // 조회 부서 미지정 시: 로드된 편성요구 행의 부서 전원 제출이면 「제출」
  const inputAvailStatus = useMemo(() => {
    const fromSearch = String(watchedDeptCode || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    const fromLines = [
      ...new Set(
        (allLines || [])
          .filter((r) => r && r.mod !== 'D')
          .map((r) => String(r.deptCode || '').trim())
          .filter(Boolean)
      ),
    ];
    return resolveIncomeInputStatusLabel(roundCtx, {
      deptCodes: fromSearch.length ? fromSearch : fromLines,
      deptSubmitMap: deptSubmitMapRef.current,
    });
    // deptSubmitTick: 제출·조회 후 맵 갱신 반영
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundCtx, watchedDeptCode, allLines, deptSubmitTick]);

  useEffect(() => {
    setValue('inputAvailStatus', inputAvailStatus);
  }, [inputAvailStatus, setValue]);

  // 차수·연도·회계구분 변경 시 이전 조회분 비움 (F7로 재조회)
  // — 회계구분은 입력 컨텍스트이므로, 다른 구분 조회분이 현재 구분으로 저장되지 않게 함
  // 빈 값 → 본예산 자동선택은 조회분을 지우지 않음 (select 표시만 되고 폼이 비어 있던 보정)
  const prevSearchCtxRef = useRef({
    kind: watchedKindCode,
    year: watchedFiscalYear,
    acct: watchedAcctClass,
  });
  useEffect(() => {
    const prev = prevSearchCtxRef.current;
    const next = {
      kind: watchedKindCode,
      year: watchedFiscalYear,
      acct: watchedAcctClass,
    };
    prevSearchCtxRef.current = next;
    const yearOrAcctChanged = prev.year !== next.year || prev.acct !== next.acct;
    const kindFromEmpty = !String(prev.kind || '').trim() && String(next.kind || '').trim();
    if (!yearOrAcctChanged && kindFromEmpty) return;
    setAllLines([]);
    allLinesRef.current = [];
    setTreeNodes([]);
    setSelectedTreeId(TREE_ROOT_ID);
    setSelectedUid('');
    selectedUidRef.current = '';
    selectedDeptCodeRef.current = '';
    deptSubmitMapRef.current = new Map();
    searchFilterRef.current = {
      deptCode: '',
      projectCode: '',
      subjectCode: '',
      fundUid: '',
      fundPkgUid: '',
      fundCode: '',
    };
    masterRef.current?.setData?.([]);
    detailRef.current?.setData?.([]);
  }, [watchedKindCode, watchedFiscalYear, watchedAcctClass]);

  const getDims = useCallback(() => {
    const v = getValues();
    return {
      tenantCode: v.tenantCode || tenantCode,
      fiscalYear: v.fiscalYear || year,
      budgetKindCode: String(v.budgetKindCode || '').trim(),
      budgetType: v.budgetType || BUDGET_TYPE_DEFAULT,
      acctClass: v.acctClass || defaultAcctClass,
      purpose: '집행',
      treeMode: v.treeMode || 'SUBJECT',
      inoutDiv: '수입',
    };
  }, [getValues, tenantCode, year, defaultAcctClass]);

  /** 요구단계 부서별 제출 여부 갱신 */
  const refreshDeptSubmitMap = useCallback(() => {
    const dims = getDims();
    deptSubmitMapRef.current = getIncomeDeptSubmitMap(
      dims.tenantCode,
      dims.fiscalYear,
      dims.budgetKindCode
    );
    setDeptSubmitTick((n) => n + 1);
  }, [getDims]);

  /** 차수 잠금이 아니고, 해당 부서가 미제출일 때만 편집 가능 */
  const isDeptEditable = useCallback((deptCode) => {
    if (!canEditRef.current) return false;
    const code = String(deptCode || '').trim();
    if (!code) return true;
    return deptSubmitMapRef.current.get(code) !== 'Y';
  }, []);
  isDeptEditableRef.current = isDeptEditable;

  const assertCanEdit = useCallback(() => {
    const dims = getDims();
    const ctx = resolveIncomeRequestContext(dims.tenantCode, dims.fiscalYear, dims.budgetKindCode);
    setRoundCtx(ctx);
    if (!ctx.canEdit) {
      showAlert(ctx.editBlockReason || '현재 예산차수에서는 입력할 수 없습니다.', 'warning');
      return false;
    }
    return true;
  }, [getDims]);

  const detailColumns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '산출레벨',
        field: 'calcLevel',
        size: 90,
        options: CALC_LEVEL_OPTIONS,
        align: 'center',
        required: true,
        editable: () => canEditRef.current && isDeptEditableRef.current(selectedDeptCodeRef.current),
      }),
      hiCol.edTextColumn({
        header: '산출내역',
        field: 'calcDesc',
        size: 200,
        editable: () => canEditRef.current && isDeptEditableRef.current(selectedDeptCodeRef.current),
      }),
      hiCol.edTextColumn({
        header: '산출기초',
        field: 'calcBasis',
        size: 200,
        title: '숫자 천단위 콤마 · 「원」생략 시에도 원→천원(수입 절사) · 「천원」표기 시 그대로',
        editable: (row) =>
          canEditRef.current &&
          isDeptEditableRef.current(selectedDeptCodeRef.current) &&
          isCalcBasisEditable(row),
        formatCalcBasis: true,
      }),
      hiCol.edNumberColumn({
        header: '산출금액(천원)',
        field: 'calcAmt',
        size: 130,
        title: '산출기초 자동계산 · 상위레벨 합산 · 수입 절사',
        editable: (row) =>
          canEditRef.current &&
          isDeptEditableRef.current(selectedDeptCodeRef.current) &&
          isCalcAmtEditable(row),
      }),
      hiCol.edTextColumn({
        header: '비고',
        field: 'remark',
        size: 140,
        editable: () => canEditRef.current && isDeptEditableRef.current(selectedDeptCodeRef.current),
      }),
    ],
    []
  );

  const masterGrid = useHiGridHook({
    gridRef: masterRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: defaultMasterRow(),
  });

  const detailGrid = useHiGridHook({
    gridRef: detailRef,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: defaultDetailRow(),
  });

  /** 현재 마스터 행의 디테일을 캐시·마스터 예산액에 반영 */
  const syncDetailToCache = useCallback((overrideDetails) => {
    const uid = selectedUidRef.current;
    if (!uid) return allLinesRef.current;
    const details = rollupCalcDetails(
      overrideDetails ?? detailRef.current?.getData?.() ?? [],
      CALC_OPTS
    );
    const budgetAmt = sumTopLevelBudgetAmt(details, CALC_OPTS);
    const dims = getDims();
    const next = (allLinesRef.current || []).map((r) => {
      if (r.requestUid !== uid) return r;
      const mod = r.mod === 'A' || r.mod === 'D' ? r.mod : 'U';
      const confirmedAmt = Number(r.confirmedAmt) || 0;
      return {
        ...r,
        details,
        budgetAmt,
        mod,
        finalAmt: deriveFinalAmt(confirmedAmt, budgetAmt, dims),
      };
    });
    allLinesRef.current = next;
    setAllLines(next);
    const masterData = masterRef.current?.getData?.() || [];
    masterRef.current?.setData?.(
      masterData.map((r) => {
        if (r.requestUid !== uid) return r;
        const mod = r.mod === 'A' || r.mod === 'D' ? r.mod : 'U';
        const confirmedAmt = Number(r.confirmedAmt) || 0;
        return {
          ...r,
          budgetAmt,
          details,
          mod,
          finalAmt: deriveFinalAmt(confirmedAmt, budgetAmt, dims),
        };
      })
    );
    return next;
  }, [getDims]);

  const showDetails = useCallback((row) => {
    if (!row) {
      detailRef.current?.setData?.([]);
      setSelectedUid('');
      selectedUidRef.current = '';
      selectedDeptCodeRef.current = '';
      return;
    }
    setSelectedUid(row.requestUid);
    selectedUidRef.current = row.requestUid;
    selectedDeptCodeRef.current = String(row.deptCode || '').trim();
    detailRef.current?.setData?.(rollupCalcDetails(row.details || [], CALC_OPTS));
  }, []);

  const captureSearchFilter = useCallback(() => {
    const v = getValues();
    const next = {
      deptCode: String(v.deptCode || '').trim(),
      projectCode: String(v.projectCode || '').trim(),
      subjectCode: String(v.subjectCode || '').trim(),
      fundUid: String(v.fundUid || '').trim(),
      fundPkgUid: String(v.fundPkgUid || '').trim(),
      fundCode: String(v.fundCode || '').trim(),
    };
    searchFilterRef.current = next;
    return next;
  }, [getValues]);

  const applySearchFilterToLines = useCallback((lines) => {
    return filterIncomeRequests(lines || [], searchFilterRef.current || {});
  }, []);

  const applyMasterFilter = useCallback(
    (lines, treeId, treeMode, nodes) => {
      let view = applySearchFilterToLines(lines || []);
      if (treeId && treeId !== TREE_ROOT_ID) {
        const node = findTreeNode(nodes, treeId);
        if (node) {
          const flat = flattenSubtree(node);
          if (treeMode === 'PROJECT') {
            const codes = new Set(flat.map((r) => r.projectCode).filter(Boolean));
            view = view.filter((r) => codes.has(r.projectCode));
          } else {
            const codes = new Set(flat.map((r) => r.subjectCode).filter(Boolean));
            view = view.filter((r) => codes.has(r.subjectCode));
          }
        }
      }
      masterRef.current?.setData?.(view.map((r) => enrichMasterRow(r)));
      if (view[0]) showDetails(view[0]);
      else showDetails(null);
    },
    [showDetails, applySearchFilterToLines]
  );

  const refreshTreeFromLines = useCallback(
    (lines, treeId = TREE_ROOT_ID, { refilterMaster = true } = {}) => {
      const dims = getDims();
      // 트리는 검색조건 반영분 기준 — 전체 라인은 allLinesRef에 유지
      const forTree = applySearchFilterToLines(lines);
      const nodes = buildIncomeRequestTreeFromLines(forTree, dims.treeMode, dims);
      setTreeNodes(nodes);
      if (!refilterMaster) return nodes;
      const nextTreeId =
        treeId && treeId !== TREE_ROOT_ID && findTreeNode(nodes, treeId) ? treeId : TREE_ROOT_ID;
      setSelectedTreeId(nextTreeId);
      applyMasterFilter(lines, nextTreeId, dims.treeMode, nodes);
      return nodes;
    },
    [getDims, applyMasterFilter, applySearchFilterToLines]
  );

  const loadTreeAndLines = useCallback(async () => {
    const opts = await syncKindOptions();
    const kind = String(getValues('budgetKindCode') || '').trim();
    const dims = { ...getDims(), budgetKindCode: kind };
    if (!dims.budgetKindCode) {
      setAllLines([]);
      allLinesRef.current = [];
      setTreeNodes([]);
      masterRef.current?.setData?.([]);
      showDetails(null);
      return {
        ok: false,
        lines: [],
        nodes: [],
        reason: opts.length
          ? '예산차수(결합코드)를 선택하세요.'
          : '예산차수가 없습니다. 예산차수등록에서 해당 연도의 차수를 등록하세요.',
      };
    }
    const ctx = resolveIncomeRequestContext(dims.tenantCode, dims.fiscalYear, dims.budgetKindCode);
    setRoundCtx(ctx);
    if (!ctx.round) {
      setAllLines([]);
      allLinesRef.current = [];
      setTreeNodes([]);
      masterRef.current?.setData?.([]);
      showDetails(null);
      return {
        ok: false,
        lines: [],
        nodes: [],
        reason: ctx.editBlockReason || ctx.reason || '예산차수가 없습니다.',
      };
    }
    // GNK 2026 본예산 — gnk_bbud2 수입 이관 시드 (저장소 비어 있을 때 1회)
    if (isGnkTenant(dims.tenantCode)) {
      const boot = await ensureGnkIncomeRequestBootstrap(dims);
      if (boot.seeded) {
        const warn = [];
        if (boot.unmatchedSubjects.length) warn.push(`과목 미매핑 ${boot.unmatchedSubjects.length}종`);
        if (boot.unmatchedProjects) warn.push(`사업 미지정 ${boot.unmatchedProjects}건 (비고 참조)`);
        // 첨부 md는 마스터×디테일 조인행 — 마스터 건수와 다름(지출 이관과 동일)
        showAlert(
          `기존 시스템 2026 본예산 수입을 이관했습니다.\n마스터 ${boot.count}건 · 산출기초(디테일) ${boot.detailCount || 0}건${
            warn.length ? `\n(${warn.join(' · ')})` : ''
          }`,
          'success'
        );
      }
    }
    let rawLines = await loadIncomeRequests(dims);
    const seed = seedLastRoundDraftIfEmpty({
      tenantCode: dims.tenantCode,
      fiscalYear: dims.fiscalYear,
      budgetKindCode: dims.budgetKindCode,
      inoutDiv: '수입',
      existingRows: rawLines,
    });
    if (seed.seeded) {
      rawLines = seed.rows;
      showAlert(
        `lastRound: 직전 차수(${seed.priorKind}${seed.priorName ? ` ${seed.priorName}` : ''}) ${seed.rows.length}건을 불러왔습니다. 수정 후 저장(F10)하세요.`,
        'info'
      );
    }
    const lines = attachBaselineAmounts(rawLines, dims);
    setAllLines(lines);
    allLinesRef.current = lines;
    // 조회 버튼/F7 시점에 FormArea 검색조건 스냅샷 후 필터
    captureSearchFilter();
    const forTree = applySearchFilterToLines(lines);
    const nodes = buildIncomeRequestTreeFromLines(forTree, dims.treeMode, dims);
    setTreeNodes(nodes);
    setSelectedTreeId(TREE_ROOT_ID);
    setExpandedIds(new Set([TREE_ROOT_ID]));
    applyMasterFilter(lines, TREE_ROOT_ID, dims.treeMode, nodes);
    refreshDeptSubmitMap();
    return { ok: true, lines, viewCount: forTree.length, nodes, ctx };
  }, [
    getDims,
    getValues,
    syncKindOptions,
    applyMasterFilter,
    showDetails,
    refreshDeptSubmitMap,
    applySearchFilterToLines,
    captureSearchFilter,
  ]);

  const handleSearch = useCallback(async () => {
    const result = await loadTreeAndLines();
    if (!result.ok) {
      showAlert(result.reason || '조회할 수 없습니다.', 'warning');
      return;
    }
    const total = result.lines?.length || 0;
    const viewN = result.viewCount != null ? result.viewCount : total;
    const dims = getDims();
    const f = searchFilterRef.current || {};
    const filterParts = [];
    if (f.deptCode) filterParts.push('부서');
    if (f.projectCode) filterParts.push('사업');
    if (f.subjectCode) filterParts.push('과목');
    if (f.fundUid || f.fundPkgUid || f.fundCode) filterParts.push('재원');
    const filterHint = filterParts.length
      ? `조건(${filterParts.join('·')})`
      : '조건없음(전체)';
    const kind = `${dims.budgetKindCode} · ${acctClassLabel(dims.acctClass, dims.tenantCode)}`;
    const editHint = result.ctx?.canEdit
      ? result.ctx.periodWarn
        ? `입력가능 · ${result.ctx.periodWarn}`
        : `입력가능 (${result.ctx.applyDateDisp || '—'} ~ ${result.ctx.inputDeadlineDisp || '—'})`
      : result.ctx?.editBlockReason || '조회만 가능';
    const countHint =
      viewN !== total ? `표시 ${viewN}건 / 전체 ${total}건` : `편성요구 ${total}건`;
    showAlert(
      `조회했습니다. [${kind}] ${countHint} · ${filterHint} · ${editHint}`,
      result.ctx?.canEdit && result.ctx?.periodOk ? 'success' : 'warning'
    );
  }, [loadTreeAndLines, getDims]);

  const handleToggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allExpandableIds = useMemo(
    () => collectAllExpandableIds(treeNodes),
    [treeNodes]
  );

  const isTreeAllExpanded = useMemo(() => {
    if (!allExpandableIds.length) return false;
    return allExpandableIds.every((id) => expandedIds.has(id));
  }, [allExpandableIds, expandedIds]);

  const handleTreeExpandCollapseAll = useCallback(() => {
    if (isTreeAllExpanded) {
      setExpandedIds(new Set());
      return;
    }
    setExpandedIds(new Set(allExpandableIds));
  }, [isTreeAllExpanded, allExpandableIds]);

  const handleSelectRoot = useCallback(() => {
    syncDetailToCache();
    setSelectedTreeId(TREE_ROOT_ID);
    applyMasterFilter(allLinesRef.current, TREE_ROOT_ID, getDims().treeMode, treeNodes);
  }, [syncDetailToCache, applyMasterFilter, getDims, treeNodes]);

  const handleSelectTreeNode = useCallback(
    (n) => {
      syncDetailToCache();
      const id = n._treeId || TREE_ROOT_ID;
      setSelectedTreeId(id);
      applyMasterFilter(allLinesRef.current, id, getDims().treeMode, treeNodes);
    },
    [syncDetailToCache, applyMasterFilter, getDims, treeNodes]
  );

  const selectMasterRow = useCallback(
    (row) => {
      if (!row?.requestUid) return;
      if (selectedUidRef.current && selectedUidRef.current !== row.requestUid) {
        syncDetailToCache();
      }
      const cached =
        (allLinesRef.current || []).find((r) => r.requestUid === row.requestUid) || row;
      showDetails(cached);
    },
    [syncDetailToCache, showDetails]
  );

  useEffect(() => {
    masterGrid.onRowClicked = ({ data }) => selectMasterRow(data);
    masterGrid.onSelectRow = (data) => selectMasterRow(data);
    masterGrid.onGridCellDoubleClicked = (data, cellInfo) => {
      selectMasterRow(data);
      if (data?.deptCode && !isDeptEditableRef.current(data.deptCode)) {
        showAlert(
          `부서 ${data.deptCode}는 이미 제출되어 변경할 수 없습니다. 「제출」버튼으로 제출취소 후 가능합니다.`,
          'warning'
        );
        return;
      }
      const field = cellInfo?.field;
      const map = {
        deptCode: 'dept',
        deptName: 'dept',
        projectCode: 'project',
        projectName: 'project',
        subjectCode: 'subject',
        subjectName: 'subject',
        subjectDispCode: 'subject',
        fundCode: 'fund',
        fundName: 'fund',
      };
      if (field && map[field]) setLookup({ open: true, type: map[field] });
    };
    masterGrid.onAfterSaveCell = (rowData, { field }) => {
      if (!rowData?.requestUid) return;
      const dims = getDims();
      const dimFields = new Set([
        'deptCode',
        'projectCode',
        'subjectCode',
        'subjectUid',
        'fundCode',
        'fundUid',
        'fundPkgUid',
      ]);
      const patched = dimFields.has(field)
        ? withBaselineAmounts(enrichMasterRow(rowData), dims)
        : rowData;
      const next = (allLinesRef.current || []).map((r) =>
        r.requestUid === patched.requestUid ? { ...r, ...patched } : r
      );
      allLinesRef.current = next;
      setAllLines(next);
      if (dimFields.has(field)) {
        const masterData = masterRef.current?.getData?.() || [];
        masterRef.current?.setData?.(
          masterData.map((r) =>
            r.requestUid === patched.requestUid ? { ...r, ...patched } : r
          )
        );
      }
      if (
        field === 'projectCode' ||
        field === 'projectName' ||
        field === 'subjectCode' ||
        field === 'subjectName'
      ) {
        refreshTreeFromLines(next, selectedTreeId, { refilterMaster: false });
      }
    };
  }, [masterGrid, selectMasterRow, refreshTreeFromLines, selectedTreeId, getDims]);

  useEffect(() => {
    detailGrid.onAfterSaveCell = (rowData, { field }) => {
      if (field !== 'calcBasis' && field !== 'calcLevel' && field !== 'calcAmt') return;
      const data = detailRef.current?.getData?.() || [];
      // 산출기초: 숫자 천단위 콤마 정규화 후 롤업
      const normalized = data.map((r) => {
        if (field === 'calcBasis' && rowData?.detailId && r.detailId === rowData.detailId) {
          return { ...r, calcBasis: formatCalcBasisCommas(r.calcBasis) };
        }
        if (field === 'calcBasis' && rowData?.id != null && r.id === rowData.id) {
          return { ...r, calcBasis: formatCalcBasisCommas(r.calcBasis) };
        }
        return r;
      });
      const rolled = rollupCalcDetails(normalized, CALC_OPTS);
      detailRef.current?.setData?.(rolled);
      syncDetailToCache(rolled);
    };
  }, [detailGrid, syncDetailToCache]);

  // Alt+` : 선택 행 산출기초 수동 잠금/해제 (합산행은 항상 잠금)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      const isBackquote = e.code === 'Backquote' || e.key === '`' || e.key === '₩';
      if (!isBackquote) return;
      if (!canEditRef.current) return;
      if (activeGridId !== DETAIL_GRID_ID) return;
      const visibleEl = document.querySelector(`[data-grid-id="${DETAIL_GRID_ID}"]`);
      if (visibleEl && !isElementVisible(visibleEl)) return;
      e.preventDefault();
      e.stopPropagation();
      const row = detailRef.current?.getSelectedRow?.();
      if (!row) {
        showAlert('산출내역 행을 선택한 뒤 Alt+` 로 잠금합니다.', 'warning');
        return;
      }
      if (row.hasChildren) {
        showAlert('하위 합산 행은 산출기초를 입력할 수 없습니다.', 'warning');
        return;
      }
      const data = [...(detailRef.current?.getData?.() || [])];
      const idx = data.findIndex((r) => {
        if (row.detailId && r.detailId === row.detailId) return true;
        if (row.id != null && row.id !== '' && r.id === row.id) return true;
        return false;
      });
      if (idx < 0) return;
      const nextLocked = !data[idx].basisLocked;
      data[idx] = {
        ...data[idx],
        basisLocked: nextLocked,
        ...(nextLocked ? { calcBasis: '' } : {}),
      };
      const rolled = rollupCalcDetails(data, CALC_OPTS);
      detailRef.current?.setData?.(rolled);
      syncDetailToCache(rolled);
      showAlert(
        nextLocked ? '산출기초 셀을 잠갔습니다. (Alt+`)' : '산출기초 잠금을 해제했습니다.',
        'success'
      );
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeGridId, syncDetailToCache]);

  const handleAddMaster = useCallback(async () => {
    if (!assertCanEdit()) return;
    const uid = createRequestUid();
    await masterGrid.handleAdd(
      defaultMasterRow({
        requestUid: uid,
        id: uid,
        budgetKindCode: getDims().budgetKindCode,
        details: [],
      })
    );
    const data = masterRef.current?.getData?.() || [];
    const row = data.find((r) => r.requestUid === uid) || data[data.length - 1];
    const next = withBaselineAmounts(
      enrichMasterRow(row || defaultMasterRow({ requestUid: uid })),
      getDims()
    );
    const merged = [...(allLinesRef.current || []).filter((r) => r.requestUid !== uid), next];
    allLinesRef.current = merged;
    setAllLines(merged);
    showDetails(next);
  }, [masterGrid, showDetails, assertCanEdit, getDims]);

  const handleDeleteMaster = useCallback(async () => {
    if (!assertCanEdit()) return;
    const focused = masterRef.current?.getSelectedRow?.();
    if (focused?.deptCode && !isDeptEditable(focused.deptCode)) {
      showAlert(
        `부서 ${focused.deptCode}는 이미 제출되어 삭제할 수 없습니다. 「제출」버튼으로 제출취소 후 가능합니다.`,
        'warning'
      );
      return;
    }
    await masterGrid.handleDelete();
    const data = masterRef.current?.getData?.() || [];
    const uids = new Set(data.map((r) => r.requestUid));
    const outside = (allLinesRef.current || []).filter((r) => !uids.has(r.requestUid));
    const merged = [...outside, ...data];
    allLinesRef.current = merged;
    setAllLines(merged);
    // 삭제 반영 후 입력 기준 트리·마스터 필터 재구성
    refreshTreeFromLines(merged, selectedTreeId);
  }, [masterGrid, refreshTreeFromLines, selectedTreeId, assertCanEdit, isDeptEditable]);

  const handleAddDetail = useCallback(async () => {
    if (!assertCanEdit()) return;
    if (!selectedUidRef.current) {
      showAlert('마스터 행을 먼저 선택하세요.', 'warning');
      return;
    }
    if (!isDeptEditable(selectedDeptCodeRef.current)) {
      showAlert(
        '제출된 부서의 산출내역은 수정할 수 없습니다. 「제출」버튼으로 제출취소 후 가능합니다.',
        'warning'
      );
      return;
    }
    const cur = detailRef.current?.getData?.() || [];
    const lastLv = cur.length ? Number(cur[cur.length - 1].calcLevel) || 1 : 1;
    await detailGrid.handleAdd(defaultDetailRow({ calcLevel: lastLv }));
    syncDetailToCache();
  }, [detailGrid, syncDetailToCache, assertCanEdit, isDeptEditable]);

  const handleDeleteDetail = useCallback(async () => {
    if (!assertCanEdit()) return;
    if (!isDeptEditable(selectedDeptCodeRef.current)) {
      showAlert(
        '제출된 부서의 산출내역은 수정할 수 없습니다. 「제출」버튼으로 제출취소 후 가능합니다.',
        'warning'
      );
      return;
    }
    await detailGrid.handleDelete();
    syncDetailToCache();
  }, [detailGrid, syncDetailToCache, assertCanEdit, isDeptEditable]);

  /** 산출내역 편집 가능 여부 (마스터·부서 제출 상태) */
  const assertDetailEditable = useCallback(() => {
    if (!assertCanEdit()) return false;
    if (!selectedUidRef.current) {
      showAlert('마스터 행을 먼저 선택하세요.', 'warning');
      return false;
    }
    if (!isDeptEditable(selectedDeptCodeRef.current)) {
      showAlert(
        '제출된 부서의 산출내역은 수정할 수 없습니다. 「제출」버튼으로 제출취소 후 가능합니다.',
        'warning'
      );
      return false;
    }
    return true;
  }, [assertCanEdit, isDeptEditable]);

  const findDetailIndex = useCallback((data, row) => {
    if (!row) return -1;
    return (data || []).findIndex((r) => {
      if (row.detailId && r.detailId === row.detailId) return true;
      if (row.id != null && row.id !== '' && r.id === row.id) return true;
      return false;
    });
  }, []);

  /** 산출금액 롤업 후 그리드·마스터 예산액 반영 */
  const applyDetailRollup = useCallback(
    (rows, { selectDetailId, selectIndex } = {}) => {
      const rolled = rollupCalcDetails(rows || [], CALC_OPTS);
      detailRef.current?.setData?.(rolled);
      syncDetailToCache(rolled);
      if (selectDetailId != null) {
        detailRef.current?.setSelectedRowById?.(selectDetailId);
      } else if (selectIndex != null && selectIndex >= 0) {
        detailRef.current?.setSelectedRowIndex?.(selectIndex);
      }
      return rolled;
    },
    [syncDetailToCache]
  );

  // 삽입: 선택 행 위
  const handleDetailInsertAbove = useCallback(async () => {
    if (!assertDetailEditable()) return;
    const selected = detailRef.current?.getSelectedRow?.();
    if (!selected) {
      showAlert('추가할 위치의 행을 선택하세요.', 'warning');
      return;
    }
    const lv = Number(selected.calcLevel) || 1;
    await detailGrid.handleAddAbove(defaultDetailRow({ calcLevel: lv }));
    applyDetailRollup(detailRef.current?.getData?.() || []);
  }, [assertDetailEditable, detailGrid, applyDetailRollup]);

  // 추가: 선택 행 아래 (미선택 시 맨 끝)
  const handleDetailInsertBelow = useCallback(async () => {
    if (!assertDetailEditable()) return;
    const cur = detailRef.current?.getData?.() || [];
    const selected = detailRef.current?.getSelectedRow?.();
    const lv = selected
      ? Number(selected.calcLevel) || 1
      : cur.length
        ? Number(cur[cur.length - 1].calcLevel) || 1
        : 1;
    await detailGrid.handleAdd(defaultDetailRow({ calcLevel: lv }));
    applyDetailRollup(detailRef.current?.getData?.() || []);
  }, [assertDetailEditable, detailGrid, applyDetailRollup]);

  // 위로/아래로 이동 + No. 순서 유지(배열 순서) + 산출금액 재계산
  const handleDetailMove = useCallback(
    (direction) => {
      if (!assertDetailEditable()) return;
      const data = [...(detailRef.current?.getData?.() || [])];
      const selected = detailRef.current?.getSelectedRow?.();
      if (!selected) {
        showAlert('이동할 행을 선택하세요.', 'warning');
        return;
      }
      const idx = findDetailIndex(data, selected);
      if (idx < 0) return;
      const target = idx + direction;
      if (target < 0 || target >= data.length) {
        showAlert(direction < 0 ? '더 이상 위로 이동할 수 없습니다.' : '더 이상 아래로 이동할 수 없습니다.', 'warning');
        return;
      }
      const next = [...data];
      const [row] = next.splice(idx, 1);
      next.splice(target, 0, row);
      const selectId = row.detailId || row.id;
      applyDetailRollup(next, { selectDetailId: selectId, selectIndex: target });
    },
    [assertDetailEditable, findDetailIndex, applyDetailRollup]
  );

  /** 검증: 산출레벨·산출기초(수식) 기준 산출금액 재계산 */
  const handleDetailVerify = useCallback(() => {
    if (!selectedUidRef.current) {
      showAlert('마스터 행을 먼저 선택하세요.', 'warning');
      return;
    }
    const data = detailRef.current?.getData?.() || [];
    if (!data.length) {
      showAlert('검증할 산출내역이 없습니다.', 'warning');
      return;
    }
    applyDetailRollup(data);
    showAlert('산출레벨·산출기초에 따라 산출금액을 재계산했습니다.', 'success');
  }, [applyDetailRollup]);

  const handleSave = useCallback(async () => {
    if (!assertCanEdit()) return;
    // 셀 편집 중 F10: blur로 커밋한 뒤 저장 (미커밋 값 누락 방지)
    const ae = document.activeElement;
    if (ae && typeof ae.blur === 'function') ae.blur();

    await new Promise((r) => setTimeout(r, 0));

    syncDetailToCache();
    const dims = getDims();
    if (!dims.budgetKindCode) {
      showAlert('예산차수(결합코드)를 선택하세요.', 'warning');
      return;
    }
    const gridData = masterRef.current?.getData?.() || [];
    const filterUids = new Set(gridData.map((r) => r.requestUid).filter(Boolean));
    const outside = (allLinesRef.current || []).filter((r) => !filterUids.has(r.requestUid));
    const merged = [...outside, ...gridData].map((r) => enrichMasterRow(r));
    if (!merged.filter((r) => r.mod !== 'D').length) {
      showAlert('저장할 편성요구 행이 없습니다. 마스터에 행을 추가하세요.', 'warning');
      return;
    }
    const v = validateIncomeRequests(merged.filter((r) => r.mod !== 'D'));
    if (!v.ok) {
      showAlert(v.message, 'warning');
      return;
    }
    const result = await saveIncomeRequests(dims, merged);
    const saved = attachBaselineAmounts(result?.rows || result || [], dims);
    const deptSync = result?.deptSync;
    setAllLines(saved);
    allLinesRef.current = saved;
    refreshTreeFromLines(saved, selectedTreeId);
    refreshDeptSubmitMap();
    const saveCtx = `${dims.budgetKindCode} · ${acctClassLabel(dims.acctClass, dims.tenantCode)}`;
    if (deptSync?.ok) {
      showAlert(
        `[${saveCtx}] 편성요구 ${saved.length}건 저장 · 부서별 조정상태(${deptSync.stageName}) ${deptSync.deptCount}개 부서 반영`,
        'success'
      );
    } else {
      showAlert(
        `[${saveCtx}] 편성요구 ${saved.length}건 저장했습니다.` +
          (deptSync?.reason ? `\n(부서별 조정상태 미반영: ${deptSync.reason})` : ''),
        deptSync?.reason ? 'warning' : 'success'
      );
    }
  }, [syncDetailToCache, getDims, refreshTreeFromLines, selectedTreeId, assertCanEdit, refreshDeptSubmitMap]);

  /** 편성요구 제출 토글 — 미제출이면 제출, 전원 제출이면 제출취소 */
  const handleSubmitRequest = useCallback(async () => {
    const dims = getDims();
    if (!dims.budgetKindCode) {
      showAlert('예산차수(결합코드)를 선택하세요.', 'warning');
      return;
    }
    const dirty = (allLinesRef.current || []).some(
      (r) => r.mod === 'A' || r.mod === 'U' || r.mod === 'D'
    );
    if (dirty) {
      showAlert('저장되지 않은 변경이 있습니다. 먼저 저장(F10)한 뒤 처리하세요.', 'warning');
      return;
    }
    refreshDeptSubmitMap();
    const allCodes = [
      ...new Set(
        (allLinesRef.current || [])
          .filter((r) => r.mod !== 'D')
          .map((r) => String(r.deptCode || '').trim())
          .filter(Boolean)
      ),
    ];
    if (!allCodes.length) {
      showAlert('대상 부서가 없습니다.', 'warning');
      return;
    }
    const submitted = allCodes.filter((c) => deptSubmitMapRef.current.get(c) === 'Y');
    const unsubmitted = allCodes.filter((c) => deptSubmitMapRef.current.get(c) !== 'Y');
    const isCancel = unsubmitted.length === 0 && submitted.length > 0;

    if (isCancel) {
      // 제출취소 — 행 잠금만 풀면 되므로 차수 확정만 아니면 가능
      const ctx = resolveIncomeRequestContext(dims.tenantCode, dims.fiscalYear, dims.budgetKindCode);
      setRoundCtx(ctx);
      if (ctx.round?.budgetConfirmYn === 'Y' || ctx.reqStage?.adjConfirmYn === 'Y') {
        showAlert(ctx.editBlockReason || '확정된 차수에서는 제출취소할 수 없습니다.', 'warning');
        return;
      }
      const ok = await showConfirm(
        `${submitted.length}개 부서의 편성요구 제출을 취소할까요?\n취소 후 해당 부서 데이터를 다시 수정할 수 있습니다.`,
        'warning'
      );
      if (!ok) return;
      const result = cancelIncomeRequestDepts(
        dims.tenantCode,
        dims.fiscalYear,
        dims.budgetKindCode,
        submitted,
        { userId: user?.id || user?.userId || '' }
      );
      if (!result.ok) {
        showAlert(result.reason || '제출취소에 실패했습니다.', 'warning');
        return;
      }
      refreshDeptSubmitMap();
      showAlert(
        `${submitted.length}개 부서 제출취소 완료 (${result.stageName || '요구단계'}). 수정할 수 있습니다.`,
        'success'
      );
      return;
    }

    if (!assertCanEdit()) return;
    const codes = unsubmitted;
    // 제출 대상 부서의 월별예산 미배분 ≠ 0 이면 제출 차단
    const monthlyMerged = buildMonthlyRowsFromMasters(
      allLinesRef.current || [],
      await loadMonthlyBudgets(dims)
    );
    const unallocRows = findUnallocRows(monthlyMerged, codes);
    if (unallocRows.length) {
      showAlert(
        `${MONTHLY_BUDGET_LABEL} 미배분 행이 ${unallocRows.length}건 있습니다. 「${MONTHLY_BUDGET_LABEL}」에서 배분을 완료한 뒤 제출하세요.`,
        'warning'
      );
      return;
    }
    const ok = await showConfirm(
      `${codes.length}개 부서의 편성요구를 제출할까요?\n제출 후에는 해당 부서 데이터를 수정할 수 없습니다. 수정이 필요하면 「제출취소」로 다시 열어 주세요.`,
      'warning'
    );
    if (!ok) return;
    const result = submitIncomeRequestDepts(
      dims.tenantCode,
      dims.fiscalYear,
      dims.budgetKindCode,
      codes,
      {
        userId: user?.id || user?.userId || '',
        incomeRows: await loadIncomeRequests({ ...dims, acctClass: '' }),
      }
    );
    if (!result.ok) {
      showAlert(result.reason || '제출에 실패했습니다.', 'warning');
      return;
    }
    refreshDeptSubmitMap();
    showAlert(
      `${codes.length}개 부서 제출 완료 (${result.stageName || '요구단계'}). 해당 부서는 수정할 수 없습니다.`,
      'success'
    );
  }, [assertCanEdit, getDims, refreshDeptSubmitMap, user]);

  /** 로드 부서가 모두 제출이면 버튼 라벨 「제출취소」 */
  const submitToggleLabel = useMemo(() => {
    const codes = [
      ...new Set(
        (allLines || [])
          .filter((r) => r && r.mod !== 'D')
          .map((r) => String(r.deptCode || '').trim())
          .filter(Boolean)
      ),
    ];
    if (!codes.length) return '제출';
    const allSubmitted = codes.every((c) => deptSubmitMapRef.current.get(c) === 'Y');
    return allSubmitted ? '제출취소' : '제출';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLines, deptSubmitTick]);

  /** 그리드 마스터 셀 코드조회 */
  const openLookup = useCallback(
    (type, row) => {
      if (!canEditRef.current) {
        showAlert(roundCtx.editBlockReason || '현재 예산차수에서는 코드를 변경할 수 없습니다.', 'warning');
        return;
      }
      if (row?.deptCode && !isDeptEditable(row.deptCode)) {
        showAlert(
          `부서 ${row.deptCode}는 이미 제출되어 변경할 수 없습니다. 「제출」버튼으로 제출취소 후 가능합니다.`,
          'warning'
        );
        return;
      }
      if (row?.requestUid) {
        selectMasterRow(row);
      } else if (!selectedUidRef.current) {
        showAlert('마스터 행을 선택한 뒤 코드 조회를 사용하세요.', 'warning');
        return;
      }
      setLookup({ open: true, type, target: 'grid' });
    },
    [selectMasterRow, roundCtx.editBlockReason, isDeptEditable]
  );

  /** 검색조건 FormArea 코드조회 — 편집 가능 여부·행 선택과 무관 */
  const openSearchLookup = useCallback(
    (type) => {
      if (type === 'dept' && !canChangeSearchDept) {
        showAlert('소속 부서만 조회할 수 있습니다. 부서 변경 권한이 없습니다.', 'warning');
        return;
      }
      setLookup({ open: true, type, target: 'search' });
    },
    [canChangeSearchDept]
  );

  /** Disp 비우면 코드·명칭도 초기화 → 해당 조건 전체 조회 */
  const clearSearchCodeFields = useCallback(
    (kind) => {
      if (kind === 'dept') {
        if (!canChangeSearchDept) return;
        setValue('deptCode', '');
        setValue('deptName', '');
        setValue('deptDisp', '');
        return;
      }
      if (kind === 'project') {
        setValue('projectCode', '');
        setValue('projectName', '');
        setValue('projectDisp', '');
        return;
      }
      if (kind === 'subject') {
        setValue('subjectCode', '');
        setValue('subjectName', '');
        setValue('subjectDisp', '');
        return;
      }
      if (kind === 'fund') {
        setValue('fundUid', '');
        setValue('fundPkgUid', '');
        setValue('fundCode', '');
        setValue('fundName', '');
        setValue('fundDisp', '');
      }
    },
    [canChangeSearchDept, setValue]
  );
  const masterColumns = useMemo(
    () => [
      // 코드열: 저장·검증용 (부서·사업·재원은 기본 숨김). ··· 는 명칭열로 이동
      hiCol.edTextColumn({
        header: '부서코드',
        field: 'deptCode',
        size: 100,
        required: true,
        editable: (row) => isDeptEditableRef.current(row?.deptCode),
      }),
      hiCol.roTextColumn({
        header: '부서명',
        field: 'deptName',
        size: 140,
        required: true,
        showSearch: true,
        searchTitle: '부서 조회',
        onSearch: ({ row }) => openLookup('dept', row),
      }),
      hiCol.edTextColumn({
        header: '사업코드',
        field: 'projectCode',
        size: 100,
        required: true,
        editable: (row) => isDeptEditableRef.current(row?.deptCode),
      }),
      hiCol.roTextColumn({
        header: '사업명',
        field: 'projectName',
        size: 160,
        required: true,
        showSearch: true,
        searchTitle: '사업 조회',
        onSearch: ({ row }) => openLookup('project', row),
      }),
      // 과목: 상급기관 표준코드 — 코드·명칭 모두 기본 표시, ··· 는 명칭
      hiCol.edTextColumn({
        header: '과목코드',
        field: 'subjectDispCode',
        size: 130,
        required: true,
        title: '세항-세목 결합코드 · 예: 721-10101',
        editable: (row) => isDeptEditableRef.current(row?.deptCode),
      }),
      hiCol.roTextColumn({
        header: '과목명',
        field: 'subjectName',
        size: 180,
        required: true,
        showSearch: true,
        searchTitle: '예산과목 조회 (결합코드)',
        onSearch: ({ row }) => openLookup('subject', row),
      }),
      hiCol.edTextColumn({
        header: '재원코드',
        field: 'fundCode',
        size: 100,
        required: true,
        editable: (row) => isDeptEditableRef.current(row?.deptCode),
      }),
      hiCol.roTextColumn({
        header: '재원명',
        field: 'fundName',
        size: 140,
        required: true,
        showSearch: true,
        searchTitle: '재원·재원구성 조회',
        onSearch: ({ row }) => openLookup('fund', row),
      }),
      hiCol.roNumberColumn({
        header: '전년도예산액',
        field: 'prevYearAmt',
        size: 110,
        title:
          '전년 동일 차원 금액 · 기관옵션(전년 본예산만/전년 최종). 저장하지 않음 · 행 삭제 시 원천 유지',
      }),
      hiCol.roNumberColumn({
        header: '확정예산액',
        field: 'confirmedAmt',
        size: 110,
        title:
          '기정: sumDelta=이전 확정(본·추·성립전·간주) 합 / lastRound=직전 확정 차수 절대액. 저장 안 함',
      }),
      hiCol.roNumberColumn({
        header: '예산액(천원)',
        field: 'budgetAmt',
        size: 120,
        title: isLastRoundMode(getDims().tenantCode)
          ? '금회 절대액(천원) · lastRound'
          : '금회 증감(천원) · sumDelta · 디테일 산출 합산',
      }),
      hiCol.roNumberColumn({
        header: '최종(참고)',
        field: 'finalAmt',
        size: 110,
        title: isLastRoundMode(getDims().tenantCode)
          ? 'lastRound: 금회 절대액'
          : 'sumDelta: 기정 + 금회 증감',
      }),
      hiCol.edTextColumn({
        header: '비고',
        field: 'remark',
        size: 140,
        editable: (row) => isDeptEditableRef.current(row?.deptCode),
      }),
    ],
    [openLookup]
  );

  const lookupConfig = useMemo(() => {
    const dims = getDims();
    const tenant = dims.tenantCode;
    const forSearch = lookup.target === 'search';

    if (lookup.type === 'dept') {
      let list = filterDepts(loadDepts(tenant), { useYn: 'Y' });
      // 검색·비마스터: 본인 소속 부서만
      if (forSearch && !canChangeSearchDept && sessionDept.deptCode) {
        list = list.filter((r) => String(r.deptCode || '').trim() === sessionDept.deptCode);
      }
      const rows = list.map((r) => ({
        ...r,
        id: r.deptUid || r.deptCode,
        code: r.deptCode,
        name: r.deptName,
      }));
      return {
        title: forSearch ? '부서 조회 (검색)' : '부서 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
          hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 200 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordLabel: '검색',
          hintCountLabel: '부서',
          keywordFields: ['deptCode', 'deptName', 'code', 'name', 'pathCode'],
        },
      };
    }
    if (lookup.type === 'project') {
      const loaded = loadOrSeedProjects(dims);
      const list = Array.isArray(loaded) ? loaded : [];
      const rows = list
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          mod: '',
          id: r.projectUid || r.projectCode,
          code: r.projectCode,
          name: r.projectName,
        }));
      return {
        title: forSearch ? '사업 조회 (검색)' : '사업 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
          hiCol.roTextColumn({ header: '레벨', field: 'levelName', size: 90 }),
          hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 180 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordLabel: '검색',
          hintCountLabel: '사업',
          keywordFields: ['projectCode', 'projectName', 'code', 'name', 'pathCode'],
        },
      };
    }
    if (lookup.type === 'subject') {
      const all = loadSubjectsByFilter({ ...dims, inoutDiv: '수입' });
      const finals = (all || []).filter((r) => r.finalYn === 'Y' && r.useYn !== 'N' && r.mod !== 'D');
      const rows = finals.map((r) => {
        const disp = buildSubjectDispCode(r, all);
        const dispName = buildSubjectDispName(r, all);
        return {
          ...r,
          id: r.subjectUid || `${r.budgetType}-${r.subjectCode}`,
          code: disp,
          name: dispName,
          subjectDispCode: disp,
          subjectDispName: dispName,
          subjectName: dispName,
          leafSubjectName: r.subjectName,
        };
      });
      return {
        title: forSearch ? '예산과목 조회 (검색)' : '예산과목 조회 (최종)',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '결합코드', field: 'subjectDispCode', size: 140 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectDispName', size: 260 }),
          hiCol.roTextColumn({ header: '레벨', field: 'levelName', size: 80 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordLabel: '검색',
          hintCountLabel: '최종과목',
          keywordFields: [
            'subjectDispCode',
            'subjectCode',
            'code',
            'subjectDispName',
            'subjectName',
            'leafSubjectName',
            'name',
          ],
        },
      };
    }
    if (lookup.type === 'fund') {
      const fundRows = loadFunds(dims)
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          id: r.fundUid || r.fundCode,
          code: r.fundCode,
          name: r.fundName,
          _kind: 'fund',
        }));
      const fundCols = [
        hiCol.roTextColumn({ header: '재원코드', field: 'fundCode', size: 100 }),
        hiCol.roTextColumn({ header: '재원명', field: 'fundName', size: 140 }),
        hiCol.roTextColumn({ header: '그룹', field: 'fundGroupNm', size: 120 }),
      ];
      const pkgRows = loadFundPackages(dims)
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          id: r.fundPkgUid || r.pkgCode,
          code: r.pkgCode,
          name: r.pkgName,
          _kind: 'package',
          memberSummary: (r.members || [])
            .map((m) => `${m.fundCode || ''}(${(Number(m.ratioBp) || 0) / 100}%)`)
            .filter(Boolean)
            .join(', '),
        }));
      return {
        title: forSearch ? '재원·재원구성 조회 (검색)' : '재원·재원구성 조회',
        rows: fundRows,
        columns: fundCols,
        tabs: [
          {
            id: 'fund',
            label: '재원',
            rows: fundRows,
            columns: fundCols,
            hint: forSearch
              ? '체크 후 [선택(체크)] · [조건 해제]로 전체 조회'
              : '단일 재원을 선택합니다.',
          },
          {
            id: 'package',
            label: '재원구성',
            rows: pkgRows,
            columns: [
              hiCol.roTextColumn({ header: '구성코드', field: 'pkgCode', size: 110 }),
              hiCol.roTextColumn({ header: '구성명', field: 'pkgName', size: 160 }),
              hiCol.roTextColumn({ header: '구성원(비율)', field: 'memberSummary', size: 260 }),
            ],
            hint: forSearch
              ? '체크 후 [선택(체크)] · [조건 해제]로 전체 조회'
              : '재원구성을 선택하면 재원별 비율 스냅샷을 저장합니다.',
          },
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordLabel: '검색',
          hintCountLabel: '재원',
          keywordFields: ['fundCode', 'fundName', 'pkgCode', 'pkgName', 'code', 'name'],
        },
      };
    }
    return { title: '코드 조회', rows: [], columns: [] };
  }, [lookup.type, lookup.target, getDims, canChangeSearchDept, sessionDept.deptCode]);

  const applyLookup = useCallback(
    (payload) => {
      if (!payload) return;

      // 검색조건 FormArea — 단건/멀티(배열) 모두 지원. 빈 배열 = 조건 해제(전체 조회)
      if (lookup.target === 'search') {
        const rows = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
        if (!rows.length) {
          if (lookup.type === 'dept') clearSearchCodeFields('dept');
          else if (lookup.type === 'project') clearSearchCodeFields('project');
          else if (lookup.type === 'subject') clearSearchCodeFields('subject');
          else if (lookup.type === 'fund') clearSearchCodeFields('fund');
          setLookup({ open: false, type: null, target: 'grid' });
          return;
        }

        if (lookup.type === 'dept') {
          if (!canChangeSearchDept) return;
          const items = rows
            .map((r) => ({
              code: String(r.deptCode || '').trim(),
              name: String(r.deptName || '').trim(),
            }))
            .filter((x) => x.code);
          setValue('deptCode', joinCodes(items.map((x) => x.code)));
          setValue('deptName', items.map((x) => x.name).filter(Boolean).join(', '));
          setValue('deptDisp', formatMultiCodeName(items));
        } else if (lookup.type === 'project') {
          const items = rows
            .map((r) => ({
              code: String(r.projectCode || '').trim(),
              name: String(r.projectName || '').trim(),
            }))
            .filter((x) => x.code);
          setValue('projectCode', joinCodes(items.map((x) => x.code)));
          setValue('projectName', items.map((x) => x.name).filter(Boolean).join(', '));
          setValue('projectDisp', formatMultiCodeName(items));
        } else if (lookup.type === 'subject') {
          const dims = getDims();
          const all = loadSubjectsByFilter({ ...dims, inoutDiv: '수입' });
          const items = rows
            .map((r) => {
              const code =
                r.subjectDispCode || buildSubjectDispCode(r, all) || r.subjectCode || '';
              const name =
                r.subjectDispName || r.subjectName || buildSubjectDispName(r, all) || '';
              const leaf = String(r.subjectCode || code).trim();
              return {
                code: String(code).trim(),
                name: String(name).trim(),
                leaf,
              };
            })
            .filter((x) => x.code);
          setValue('subjectCode', joinCodes(items.flatMap((x) => [x.leaf, x.code].filter(Boolean))));
          setValue('subjectName', items.map((x) => x.name).filter(Boolean).join(', '));
          setValue('subjectDisp', formatMultiCodeName(items));
        } else if (lookup.type === 'fund') {
          const fundItems = [];
          const pkgItems = [];
          for (const r of rows) {
            const isPkg =
              r._lookupTab === 'package' || r._kind === 'package' || r.fundPkgUid;
            if (isPkg && (r.fundPkgUid || r.pkgCode)) {
              const code = String(r.pkgCode || '').trim();
              if (!code) continue;
              pkgItems.push({
                uid: String(r.fundPkgUid || '').trim(),
                code,
                name: String(r.pkgName || '').trim(),
              });
            } else {
              const code = String(r.fundCode || '').trim();
              if (!code) continue;
              fundItems.push({
                uid: String(r.fundUid || '').trim(),
                code,
                name: String(r.fundName || '').trim(),
              });
            }
          }
          // uid·코드를 모두 검색키로 보관 (매칭 누락 방지)
          setValue(
            'fundUid',
            joinCodes(fundItems.flatMap((x) => [x.uid, x.code].filter(Boolean)))
          );
          setValue(
            'fundPkgUid',
            joinCodes(pkgItems.flatMap((x) => [x.uid, x.code].filter(Boolean)))
          );
          const dispItems = [
            ...fundItems.map((x) => ({ code: x.code, name: x.name })),
            ...pkgItems.map((x) => ({ code: x.code, name: x.name })),
          ];
          setValue('fundCode', joinCodes(dispItems.map((x) => x.code)));
          setValue('fundName', dispItems.map((x) => x.name).filter(Boolean).join(', '));
          setValue('fundDisp', formatMultiCodeName(dispItems));
        }
        setLookup({ open: false, type: null, target: 'grid' });
        return;
      }

      const row = Array.isArray(payload) ? payload[0] : payload;
      if (!row) return;
      const uid = selectedUidRef.current;
      if (!uid) return;
      let patch = {};
      if (lookup.type === 'dept') {
        patch = { deptCode: row.deptCode, deptName: row.deptName };
      } else if (lookup.type === 'project') {
        patch = {
          projectCode: row.projectCode,
          projectName: row.projectName,
        };
      } else if (lookup.type === 'subject') {
        const dims = getDims();
        const all = loadSubjectsByFilter({ ...dims, inoutDiv: '수입' });
        const disp = row.subjectDispCode || buildSubjectDispCode(row, all);
        patch = {
          subjectUid: row.subjectUid || '',
          subjectCode: row.subjectCode || '',
          subjectDispCode: disp,
          subjectName: row.subjectName || '',
        };
      } else if (lookup.type === 'fund') {
        const isPkg = row._lookupTab === 'package' || row._kind === 'package' || row.fundPkgUid;
        if (isPkg && (row.fundPkgUid || row.pkgCode)) {
          const members = row.members || [];
          patch = {
            fundPkgUid: row.fundPkgUid || '',
            fundCode: row.pkgCode || '',
            fundName: row.pkgName || '',
            fundUid: members[0]?.fundUid || '',
            fundAmounts: members.map((m) => ({
              fundUid: m.fundUid,
              ratioBp: Number(m.ratioBp) || 0,
              amount: null,
            })),
          };
        } else {
          patch = {
            fundUid: row.fundUid || '',
            fundCode: row.fundCode || '',
            fundName: row.fundName || '',
            fundPkgUid: '',
            fundAmounts: row.fundUid
              ? [{ fundUid: row.fundUid, ratioBp: 10000, amount: null }]
              : [],
          };
        }
      }
      const masterData = masterRef.current?.getData?.() || [];
      const dims = getDims();
      const applyPatch = (r) => {
        if (r.requestUid !== uid) return r;
        return withBaselineAmounts(enrichMasterRow({ ...r, ...patch }), dims);
      };
      masterRef.current?.setData?.(masterData.map(applyPatch));
      const next = (allLinesRef.current || []).map(applyPatch);
      allLinesRef.current = next;
      setAllLines(next);
      if (lookup.type === 'project' || lookup.type === 'subject') {
        refreshTreeFromLines(next, selectedTreeId);
      }
    },
    [
      lookup.type,
      lookup.target,
      refreshTreeFromLines,
      selectedTreeId,
      getDims,
      canChangeSearchDept,
      setValue,
      clearSearchCodeFields,
    ]
  );

  const treeModeReadyRef = useRef(false);
  // 사업/품목 구분 변경 시 — 동일 마스터로 트리 축만 전환
  useEffect(() => {
    if (!treeModeReadyRef.current) {
      treeModeReadyRef.current = true;
      return;
    }
    refreshTreeFromLines(allLinesRef.current, TREE_ROOT_ID);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- treeMode 변경 시에만
  }, [watchedTreeMode]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: CommonCss.w80,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'budgetKindCode',
          label: '예산차수',
          type: 'select',
          width: CommonCss.w160,
          labelWidth: SEARCH_LABEL_W,
          options: kindOptions.length
            ? [{ value: '', label: '선택' }, ...kindOptions]
            : [{ value: '', label: '(차수 없음 — 예산차수등록 필요)' }],
          title: '결합코드 기준 · 예: 100 본예산, 210 추경1차',
          required: true,
        },
        {
          name: 'treeMode',
          label: '사업/품목',
          type: 'select',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
          options: TREE_MODE_OPTIONS,
          title: '좌측 트리: 마스터 입력 사업·과목을 확정 구조 경로로 표시',
        },
        {
          name: 'acctClass',
          label: '회계구분',
          type: 'select',
          width: CommonCss.w110,
          labelWidth: SEARCH_LABEL_W,
          options: classOptions,
          title: '회계구분등록 마스터 — 저장 시 편성 라인에 필수 기록',
          required: true,
        },
        {
          name: 'applyDateDisp',
          label: '적용일',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          title: '예산차수등록 · 적용일자',
        },
        {
          name: 'inputDeadlineDisp',
          label: '입력마감',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          title: '예산차수등록 · 요구단계 입력마감일',
        },
        {
          name: 'inputAvailStatus',
          label: '',
          type: 'plain',
          title: '현재 입력 가능 상태 (확정·제출·적용일·마감)',
          style: {
            color: inputAvailStatusColor(inputAvailStatus),
            fontWeight: 600,
            marginLeft: '-8px',
          },
        },
      ],
      [
        {
          name: 'deptDisp',
          label: '부서',
          type: 'text',
          width: CommonCss.w180,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: canChangeSearchDept,
          searchTitle: '부서 조회',
          onSearch: () => openSearchLookup('dept'),
          title: canChangeSearchDept
            ? '멀티체크 선택 · [조건 해제]로 전체 조회'
            : '본인 소속 부서만 조회 (변경 불가)',
          placeholder: canChangeSearchDept ? '전체' : '',
        },
        {
          name: 'projectDisp',
          label: '사업',
          type: 'text',
          width: CommonCss.w200,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '사업 조회',
          onSearch: () => openSearchLookup('project'),
          title: '멀티체크 선택 · [조건 해제]로 전체 조회',
          placeholder: '전체',
        },
        {
          name: 'subjectDisp',
          label: '과목',
          type: 'text',
          width: CommonCss.w220,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '예산과목 조회',
          onSearch: () => openSearchLookup('subject'),
          title: '멀티체크 선택 · [조건 해제]로 전체 조회',
          placeholder: '전체',
        },
        {
          name: 'fundDisp',
          label: '재원',
          type: 'text',
          width: CommonCss.w200,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '재원·재원구성 조회',
          onSearch: () => openSearchLookup('fund'),
          title: '멀티체크 선택 · [조건 해제]로 전체 조회',
          placeholder: '전체',
        },
      ],
    ],
    [kindOptions, classOptions, canChangeSearchDept, openSearchLookup, inputAvailStatus]
  );

  const getMasterExport = useCallback(
    () => masterRef.current?.getFilteredData?.() ?? masterRef.current?.getData?.() ?? [],
    []
  );
  const getMasterCols = useCallback(() => masterRef.current?.getDataColumns?.() ?? [], []);
  // 디테일 엑셀 — 캐시된 전 라인의 산출내역을 requestUid와 함께 펼친다
  // (화면에 보이는 건 선택 마스터 1건뿐이므로, 병합 시 전 마스터와 1:N 조인하려면 전체 필요)
  const getDetailExport = useCallback(() => {
    const lines = allLinesRef.current || [];
    const out = [];
    for (const m of lines) {
      if (m.mod === 'D' || !m.requestUid) continue;
      // 현재 선택 마스터는 그리드에 편집 중인 최신값을 우선
      const live =
        m.requestUid === selectedUidRef.current
          ? detailRef.current?.getData?.() ?? m.details ?? []
          : m.details || [];
      for (const d of live) {
        if (d?.mod === 'D') continue;
        out.push({ ...d, requestUid: m.requestUid });
      }
    }
    return out;
  }, []);
  const getDetailCols = useCallback(() => detailRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: MASTER_GRID_ID,
    label: '편성요구',
    getData: getMasterExport,
    getDataColumns: getMasterCols,
    joinKeys: ['requestUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: DETAIL_GRID_ID,
    label: '산출내역',
    getData: getDetailExport,
    getDataColumns: getDetailCols,
    joinKeys: ['requestUid'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '수입예산편성요구',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel
          ? `추가 → ${activeLabel} (F8)`
          : '추가 (F8) · 그리드 선택 Alt+F2',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel
          ? `삭제 → ${activeLabel} (F9)`
          : '삭제 (F9) · 그리드 선택 Alt+F2',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
        title: '저장 (F10) · 화면 전체',
      },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [
      handleSearch,
      handleActiveAdd,
      handleActiveDelete,
      handleSave,
      openExcelDialog,
      activeLabel,
    ]
  );

  const handleOpenMonthlyBudget = useCallback(() => {
    const dims = getDims();
    if (!dims.budgetKindCode) {
      showAlert('예산차수(결합코드)를 선택하세요.', 'warning');
      return;
    }
    const dirty = (allLinesRef.current || []).some(
      (r) => r.mod === 'A' || r.mod === 'U' || r.mod === 'D'
    );
    if (dirty) {
      showAlert(
        `저장되지 않은 변경이 있습니다. 먼저 저장(F10)한 뒤 ${MONTHLY_BUDGET_LABEL}을 열어 주세요.`,
        'warning'
      );
      return;
    }
    // F7 미실행 시에도 현재 Form 값으로 스냅샷 (이미 조회했다면 searchFilterRef 우선)
    const live = captureSearchFilter();
    const v = getValues();
    setMonthlyPayload({
      dims: { ...dims },
      masterRows: [...(allLinesRef.current || [])],
      initialSearch: {
        ...live,
        acctClass: String(v.acctClass || dims.acctClass || '').trim(),
        deptName: String(v.deptName || '').trim(),
        deptDisp: String(v.deptDisp || '').trim(),
        projectName: String(v.projectName || '').trim(),
        projectDisp: String(v.projectDisp || '').trim(),
        subjectName: String(v.subjectName || '').trim(),
        subjectDisp: String(v.subjectDisp || '').trim(),
        fundName: String(v.fundName || '').trim(),
        fundDisp: String(v.fundDisp || '').trim(),
      },
    });
    setMonthlyOpen(true);
  }, [getDims, captureSearchFilter, getValues]);

  const extraButtons = useMemo(
    () => [
      {
        id: 'monthlyBudget',
        label: MONTHLY_BUDGET_LABEL,
        onClick: handleOpenMonthlyBudget,
        title: `${MONTHLY_BUDGET_LABEL} · 예산액 옆 절감액 · 월/분기/반기/연 배분`,
      },
      {
        id: 'submit',
        label: submitToggleLabel,
        onClick: handleSubmitRequest,
        variant: submitToggleLabel === '제출취소' ? 'secondary' : 'primary',
        title:
          submitToggleLabel === '제출취소'
            ? '제출취소 · 해당 부서 다시 수정 가능'
            : '편성요구 제출 · 제출 후 해당 부서 수정 불가 · 다시 누르면 제출취소',
      },
    ],
    [handleOpenMonthlyBudget, handleSubmitRequest, submitToggleLabel]
  );

  const treeRootLabel =
    watchedTreeMode === 'PROJECT' ? '사업(정책·단위·세부·내역)' : '품목(관·항·세항·목·세목)';

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <CollapsibleSearchPanel
          storageKey="budget-income-request"
          prefsUserId={prefsUserId}
          summary="연도·차수·부서·사업·과목·재원"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        예산차수:{' '}
        <strong style={{ color: uiColor.primary }}>
          {watchedKindCode || '—'}
          {roundCtx.round?.roundName ? ` ${roundCtx.round.roundName}` : ''}
        </strong>
        {' · '}
        적용일 {roundCtx.applyDateDisp || '—'} ~ 입력마감 {roundCtx.inputDeadlineDisp || '—'}
        {roundCtx.periodWarn ? (
          <>
            {' · '}
            <span style={{ color: uiColor.warning }}>{roundCtx.periodWarn}</span>
          </>
        ) : null}
        {' · '}
        {inputAvailStatus ? (
          <strong
            style={{ color: inputAvailStatusColor(inputAvailStatus) }}
            title={
              !roundCtx.canEdit
                ? roundCtx.editBlockReason || ''
                : inputAvailStatus === '제출'
                  ? '조회·표시 부서가 모두 제출됨 · 행 단위 수정 불가'
                  : ''
            }
          >
            {inputAvailStatus}
          </strong>
        ) : roundCtx.canEdit ? (
          <strong style={{ color: uiColor.success }}>입력가능</strong>
        ) : (
          <strong style={{ color: uiColor.danger }} title={roundCtx.editBlockReason || ''}>
            입력잠금
          </strong>
        )}
        {' · '}
        제출된 부서는 행 단위 잠금 (「제출취소」 후 재입력)
        {' · '}
        활성그리드:{' '}
        <strong style={{ color: uiColor.primary }}>{activeLabel || '—'}</strong>
        {' (F8/F9) · F10 저장 · Alt+F2 전환'}
      </Meta>

      <SplitLayoutContainer style={{ flex: 1, minHeight: 0 }}>
        <LeftArea style={{ flex: '0 0 280px', width: 280, minWidth: 240, maxWidth: 320 }}>
          <TreePanel>
            <PanelHead>
              <span>{treeRootLabel}</span>
              <TreeHeadActions>
                <TreeToggleBtn
                  type="button"
                  $active={isTreeAllExpanded}
                  title={isTreeAllExpanded ? '전체 접기' : '전체 펼치기'}
                  onClick={handleTreeExpandCollapseAll}
                >
                  {isTreeAllExpanded ? '접기' : '펼치기'}
                </TreeToggleBtn>
              </TreeHeadActions>
            </PanelHead>
            <StructureTree
              rootLabel={`전체 ${treeRootLabel}`}
              nodes={treeNodes}
              expandedIds={expandedIds}
              selectedTreeId={selectedTreeId}
              onToggle={handleToggleExpand}
              onSelectRoot={handleSelectRoot}
              onSelectNode={handleSelectTreeNode}
            />
          </TreePanel>
        </LeftArea>

        <RightArea>
          <RightStack>
            <GridPanel $flex={1.15}>
              <ActiveGridHeader
                gridId={MASTER_GRID_ID}
                extra={`차수 ${watchedKindCode || '—'} · 재원/구성 팝업`}
              >
                편성요구 (마스터)
              </ActiveGridHeader>
              <GridBody>
                <ActiveGridScope
                  gridId={MASTER_GRID_ID}
                  label="편성요구"
                  order={0}
                  onAdd={handleAddMaster}
                  onDelete={handleDeleteMaster}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <GridExportFocusScope
                    gridId={MASTER_GRID_ID}
                    style={{ flex: 1, minHeight: 0, height: '100%' }}
                  >
                    <HiGridComponent
                      ref={masterRef}
                      columns={masterColumns}
                      height="100%"
                      showRowNumber
                      rowNumberSize={56}
                      showModColumn
                      headerActive={activeGridId === MASTER_GRID_ID}
                      {...masterGrid.contextMenuProps}
                      enableHeaderMenu
                      enableColumnFilters
                      enableFreeze
                      enableQuickExport
                      exportFileName="수입편성요구"
                      gridId={MASTER_GRID_ID}
                      prefsUserId={prefsUserId}
                      defaultHiddenColumnIds={MASTER_DEFAULT_HIDDEN_COLS}
                      colOptions={masterGrid.colOptions}
                    />
                  </GridExportFocusScope>
                </ActiveGridScope>
              </GridBody>
            </GridPanel>

            <GridPanel $flex={1}>
              <ActiveGridHeader
                gridId={DETAIL_GRID_ID}
                extra="합산행 산출기초 잠금 · Alt+` 수동잠금 · 3→2→1 → 마스터 예산액"
              >
                산출내역 (디테일)
              </ActiveGridHeader>
              <DetailToolbar>
                <ActionButton
                  type="button"
                  onClick={handleDetailInsertAbove}
                  title="선택 행 위에 추가"
                >
                  ▲ 추가
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={handleDetailInsertBelow}
                  title="선택 행 아래 추가"
                >
                  ▼ 추가
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={() => handleDetailMove(-1)}
                  title="한 행 위로 이동 · 산출금액 자동 재계산"
                >
                  ↑ 위로
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={() => handleDetailMove(1)}
                  title="한 행 아래로 이동 · 산출금액 자동 재계산"
                >
                  ↓ 아래로
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={handleDetailVerify}
                  title="산출레벨·산출기초 기준 산출금액 재계산"
                  $variant="primary"
                >
                  검증
                </ActionButton>
              </DetailToolbar>
              <GridBody>
                <ActiveGridScope
                  gridId={DETAIL_GRID_ID}
                  label="산출내역"
                  order={1}
                  onAdd={handleAddDetail}
                  onDelete={handleDeleteDetail}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <GridExportFocusScope
                    gridId={DETAIL_GRID_ID}
                    style={{ flex: 1, minHeight: 0, height: '100%' }}
                  >
                    <HiGridComponent
                      ref={detailRef}
                      columns={detailColumns}
                      height="100%"
                      showRowNumber
                      rowNumberSize={56}
                      showModColumn
                      headerActive={activeGridId === DETAIL_GRID_ID}
                      {...detailGrid.contextMenuProps}
                      enableHeaderMenu
                      enableColumnFilters
                      enableFreeze
                      enableQuickExport
                      exportFileName="산출내역"
                      gridId={DETAIL_GRID_ID}
                      prefsUserId={prefsUserId}
                      colOptions={detailGrid.colOptions}
                    />
                  </GridExportFocusScope>
                </ActiveGridScope>
              </GridBody>
            </GridPanel>
          </RightStack>
        </RightArea>
      </SplitLayoutContainer>

      <CodeLookupDialog
        open={lookup.open}
        title={lookupConfig.title}
        columns={lookupConfig.columns}
        rows={lookupConfig.rows}
        tabs={lookupConfig.tabs}
        enableSearch={!!lookupConfig.enableSearch}
        searchConfig={lookupConfig.searchConfig}
        multiSelect={lookup.target === 'search'}
        prefsUserId={prefsUserId}
        sizeKey={lookup.type ? `codeLookup-${lookup.type}` : 'codeLookup'}
        onSelect={applyLookup}
        onClose={() => setLookup({ open: false, type: null, target: 'grid' })}
      />

      <MonthlyBudgetDialog
        open={monthlyOpen}
        onClose={() => {
          setMonthlyOpen(false);
          setMonthlyPayload(null);
        }}
        dims={monthlyPayload?.dims || getDims()}
        masterRows={monthlyPayload?.masterRows || []}
        initialSearch={monthlyPayload?.initialSearch || null}
        readOnly={!roundCtx.canEdit}
        prefsUserId={prefsUserId}
      />

      {excelDialog}
    </PageContainer>
  );
};

const BudgetIncomeRequest = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <BudgetIncomeRequestInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default BudgetIncomeRequest;
